import { useState } from 'react';
import { BellRing, Trash2, Play } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useEmpresas } from '@/hooks/useEmpresa';
import {
  useBuscasSalvas, useCriarBuscaSalva, useAtualizarBuscaSalva, useExcluirBuscaSalva,
  type BuscaSalvaFiltros,
} from '@/hooks/useBuscasSalvas';
import { listarFiltrosAtivos } from './filtrosAtivos';

interface BuscasSalvasModalProps {
  isOpen: boolean;
  onClose: () => void;
  filtrosAtuais: BuscaSalvaFiltros;
  onAplicar: (filtros: BuscaSalvaFiltros) => void;
}

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-success/50 focus:border-ds-success/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';

function BuscasSalvasModal({ isOpen, onClose, filtrosAtuais, onAplicar }: BuscasSalvasModalProps) {
  const [empresaId, setEmpresaId] = useState('');
  const [nome, setNome] = useState('');

  const { data: empresas } = useEmpresas();
  const { data: buscas, isLoading } = useBuscasSalvas();
  const criarMutation = useCriarBuscaSalva();
  const atualizarMutation = useAtualizarBuscaSalva();
  const excluirMutation = useExcluirBuscaSalva();

  const empresaNome = (id: number) => empresas?.find((e) => e.id === id)?.razao_social || `Empresa #${id}`;

  // O JSONB de `buscas_salvas.filtros` é livre: salvamos o conjunto completo
  // de filtros da tela, inclusive geografia, modalidades, órgão e os
  // diferenciais de IA. O resumo abaixo mostra exatamente o que vai ser salvo.
  const resumoAtual = listarFiltrosAtivos(filtrosAtuais, { empresaNome });

  const salvar = async () => {
    const empresaIdNum = parseInt(empresaId, 10);
    if (!empresaIdNum || !nome.trim()) return;

    await criarMutation.mutateAsync({ empresa_id: empresaIdNum, nome: nome.trim(), filtros: filtrosAtuais });
    setNome('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Buscas salvas e alertas">
      <div className="space-y-5">
        <div className="space-y-3">
          <p className="flex items-start gap-2 rounded-lg border border-ds-brand/40 bg-ds-brand/5 px-3 py-2 text-sm text-ds-text-primary">
            <BellRing className="h-4 w-4 shrink-0 text-ds-brand mt-0.5" aria-hidden="true" />
            <span>
              <strong>Toda busca salva ativa gera alerta automático.</strong>{' '}
              O sistema reaplica estes filtros todos os dias e avisa você no sino de alertas
              assim que uma nova licitação compatível aparece. Desmarque “Alerta ativo”
              para guardar a busca sem receber avisos.
            </span>
          </p>

          <div>
            <p className="text-xs font-medium text-ds-text-secondary mb-1.5">
              {resumoAtual.length > 0
                ? `Filtros que serão salvos (${resumoAtual.length})`
                : 'Nenhum filtro ativo — a busca salva cobriria todas as licitações.'}
            </p>
            {resumoAtual.length > 0 && (
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                {resumoAtual.map((chip) => (
                  <span
                    key={chip.id}
                    className="rounded-full border border-ds-border bg-ds-surface2 px-2 py-0.5 text-xs text-ds-text-secondary"
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
            <select
              aria-label="Empresa"
              className={selectClassName}
              value={empresaId}
              onChange={(e) => setEmpresaId(e.target.value)}
            >
              <option value="">Selecione a empresa</option>
              {(empresas || []).map((empresa) => (
                <option key={empresa.id} value={empresa.id}>{empresa.razao_social}</option>
              ))}
            </select>

            <Input
              placeholder="Nome da busca"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />

            <Button
              type="button"
              disabled={!empresaId || !nome.trim()}
              isLoading={criarMutation.isPending}
              onClick={salvar}
            >
              Salvar busca
            </Button>
          </div>

          {criarMutation.isError && (
            <p className="text-sm text-ds-danger">Falha ao salvar a busca. Tente novamente.</p>
          )}
        </div>

        <div className="border-t border-ds-border pt-4 space-y-2">
          <h4 className="text-sm font-medium text-ds-text-primary">Minhas buscas salvas</h4>

          {isLoading && <p className="text-sm text-ds-text-secondary">Carregando...</p>}

          {!isLoading && (buscas || []).length === 0 && (
            <p className="text-sm text-ds-text-secondary">Nenhuma busca salva ainda.</p>
          )}

          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {(buscas || []).map((busca) => (
              <li
                key={busca.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ds-text-primary truncate">{busca.nome}</p>
                  <p className="text-xs text-ds-text-secondary truncate">
                    {empresaNome(busca.empresa_id)}
                    {' · '}
                    {listarFiltrosAtivos(busca.filtros || {}).length} filtros
                    {busca.ativo ? ' · alerta automático ligado' : ' · sem alerta'}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <label
                    className="flex items-center gap-1.5 text-xs text-ds-text-secondary cursor-pointer"
                    title="Quando ativa, esta busca gera alerta automático de novas licitações"
                  >
                    <input
                      type="checkbox"
                      aria-label={`Alerta automático de ${busca.nome}`}
                      checked={busca.ativo}
                      onChange={(e) => atualizarMutation.mutate({ id: busca.id, ativo: e.target.checked })}
                      className="accent-ds-success w-3.5 h-3.5"
                    />
                    Alerta ativo
                  </label>

                  <button
                    type="button"
                    aria-label={`Aplicar filtros de ${busca.nome}`}
                    title="Aplicar filtros"
                    onClick={() => onAplicar(busca.filtros)}
                    className="text-ds-text-secondary hover:text-ds-success transition-colors"
                  >
                    <Play className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    aria-label={`Excluir ${busca.nome}`}
                    title="Excluir"
                    onClick={() => excluirMutation.mutate(busca.id)}
                    className="text-ds-text-secondary hover:text-ds-danger transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
}

export { BuscasSalvasModal };
